import classNames from 'classnames';
import { isBoolean } from 'lodash';

import { registerClass } from '../SatCoreRegistry';

import Auth from '../managers/AuthManager';
import { ASSIGNMENT_MENU_STATUS, ASSIGNMENT_STATUS, PDF_DELIVERY_FORMATS } from '../managers/AssignmentManager';
import { CONTENT_ITEM_TYPES } from '../managers/ContentManager';
import gradebookManager from '../managers/GradebookManager';
import studentContentCardManager from '../managers/StudentContentCardManager';

import { getTimezoneAbbreviation, isSameDayInTimezone, isWallClockBeforeInTimezone } from '../utils/dateTimezone';

/**
 * StudentAssignmentService
 *
 * Helpers for StudentAssignment related components and supporting layers.
 *
 * This is intentionally separate from AssignmentService.
 *
 * Some student-only assignment logic may still live in other services (such
 * as AssignmentService), but when maintaining or adding new student assignment
 * specific logic, it would be ideal to favor this service when possible.
 */
export default class StudentAssignmentService {
  /**
   * Helper for determining and returning the title for the student assignment card.
   */
  static getStudentAssignmentTitle(assignment, { preferStudentAssignmentNickname = false } = {}) {
    if (!assignment) {
      return '';
    }
    if (preferStudentAssignmentNickname && assignment.nickname) {
      return assignment.nickname;
    }
    return assignment.resourceWebTitle || assignment.name || '';
  }

  /**
   * Helper for determining and returning the status label key for the student assignment card's status indicator.
   */
  static getStudentAssignmentStatusLabelKey(assignment) {
    if (!assignment) {
      return null;
    }
    // 4-bucket categorization for the new card; ASSIGNMENT_STATUS.getFlag returns the full 9-status set instead.
    switch (assignment.status) {
      case ASSIGNMENT_STATUS.STARTED:
        return 'started';
      case ASSIGNMENT_STATUS.READY:
      case ASSIGNMENT_STATUS.LOCKED:
        return 'notStarted';
      case ASSIGNMENT_STATUS.CLOSED:
      case ASSIGNMENT_STATUS.COMPLETED:
        return assignment.submitted ? 'submitted' : 'late';
      default:
        return null;
    }
  }

  /**
   * Helper for determining and returning the due-date label info for the student assignment card.
   */
  static getStudentAssignmentDueDateLabel(assignment, { now = Date.now(), timezone } = {}) {
    if (!assignment || !assignment.timezoneEndDate) {
      return { dueDateText: '', dueDateType: 'dueSpecificDate', timezoneAbbreviation: undefined };
    }
    const dueDate = new Date(`${assignment.timezoneEndDate} ${assignment.timezoneEndTime}`);
    const dueDateText = dueDate.toLocaleDateString();
    const timezoneAbbreviation = getTimezoneAbbreviation(now, timezone);

    const isUnsubmittedOpen = !assignment.submitted
      && assignment.status !== ASSIGNMENT_STATUS.COMPLETED
      && assignment.status !== ASSIGNMENT_STATUS.CLOSED;

    const isLate = isWallClockBeforeInTimezone(
      assignment.timezoneEndDate,
      assignment.timezoneEndTime,
      now,
      timezone
    );
    const isSameDay = isSameDayInTimezone(dueDate, now, timezone);

    if (isLate && isUnsubmittedOpen) {
      return { dueDateText, dueDateType: 'late', timezoneAbbreviation };
    }
    if (isSameDay) {
      return { dueDateText, dueDateType: 'dueToday', timezoneAbbreviation };
    }
    return { dueDateText, dueDateType: 'dueSpecificDate', timezoneAbbreviation };
  }

  /**
   * Return the primary action button config for a student assignment card,
   * derived from assignment.status.
   */
  static getStudentAssignmentPrimaryActionButtonConfig(assignment) {
    if (!assignment) {
      return { actionKey: null, className: '', disabled: true, labelKey: null, lockedMessageKey: null };
    }

    const isFileResource = assignment.contentItemEntityTypeId === CONTENT_ITEM_TYPES.FILE_RESOURCE;
    const isReviewStatus = assignment.status === ASSIGNMENT_STATUS.CLOSED
      || assignment.status === ASSIGNMENT_STATUS.COMPLETED;

    let config;
    if (assignment.status === ASSIGNMENT_STATUS.LOCKED) {
      config = {
        actionKey: isFileResource ? 'download' : 'launch',
        className: 'pseudo-disabled',
        disabled: true,
        labelKey: isFileResource ? 'download' : 'start',
        lockedMessageKey: this.getLockedMessageKey(assignment)
      };
    } else if (isReviewStatus) {
      config = this.getReviewAssignmentPrimaryActionButtonConfig(assignment, { isFileResource });
    } else if (this.isLaunchableAssignment(assignment)) {
      const labelKey = this.getLaunchableAssignmentLabelKey(assignment, isFileResource);
      config = {
        actionKey: isFileResource ? 'download' : 'launch',
        className: classNames({ 'turn-in-late': labelKey === 'turnInLate' }),
        disabled: false,
        labelKey,
        lockedMessageKey: null
      };
    } else {
      // Unexpected assignment.status; warn to surface to engineering and render no button.
      console.warn('[StudentAssignmentService] unexpected assignment.status:', assignment.status, 'id:', assignment.id);
      config = { actionKey: null, className: '', disabled: true, labelKey: null, lockedMessageKey: null };
    }

    // Non-content-viewer-compatible assignments show 'View' instead of the launch label.
    // File-resource downloads keep their 'download' label.
    if (config.actionKey === 'launch' && !assignment.contentViewerCompatible) {
      config = { ...config, labelKey: 'studentView' };
    }

    // License-expired: keep the resolved labelKey but force pseudo-disabled with a
    // generic rollover — student-facing wording avoids 'license' (that stays in
    // expiredLicenseButtonViewMsg for teacher-facing surfaces).
    if (assignment.licenseExpired && !!config.labelKey) {
      return {
        ...config,
        actionKey: null,
        className: 'pseudo-disabled',
        disabled: true,
        lockedMessageKey: 'expiredLicenseMessage'
      };
    }
    return config;
  }

  /**
   * Return the primary action button config for a student assignment whose
   * status is CLOSED or COMPLETED (past the deadline, review-context).
   *
   * Based on the logic in StudentContentCardManager.getAssignmentStatusValues, but expanded to be
   * config-based and to provide more specific button labels + rollovers per state.
   *
   * Kept as a separate method for now (rather than replacing the original) to avoid unexpected side-effects in
   * older components that still rely on the original logic.
   */
  static getReviewAssignmentPrimaryActionButtonConfig(assignment, { isFileResource = false } = {}) {
    // File resources are always downloadable regardless of review state.
    if (isFileResource) {
      return { actionKey: 'download', className: '', disabled: false, labelKey: 'download', lockedMessageKey: null };
    }

    const isPdfResource = assignment.contentItemEntityTypeId === CONTENT_ITEM_TYPES.PDF_RESOURCE;
    const isFlowpaperDelivery = assignment.pdfDeliveryFormat === PDF_DELIVERY_FORMATS.FLOWPAPER
      || assignment.pdfDeliveryFormat === PDF_DELIVERY_FORMATS.PDF_AND_FLOWPAPER;
    const isDocReader = assignment.contentItemEntityTypeId === CONTENT_ITEM_TYPES.DOCREADER;
    const showLockedUntilGraded = (isPdfResource && isFlowpaperDelivery) || isDocReader;
    const hasAssignmentGrade = typeof assignment.grade === 'number';
    const allowReview = (showLockedUntilGraded && assignment.hasManualScoredItems) ? hasAssignmentGrade : true;

    // A submitted assignment (submitted=true) cannot be turned in late even if studentSubmitted is false
    // (e.g. auto-submitted at due date). Old code gated the late-submit branch on !submitted for the same reason.
    const canTurnInLate = (
      !assignment.submitted
      && assignment.allowLateSubmission
      && !assignment.studentSubmitted
      && assignment.lateSubmitDate
      && new Date() < new Date(assignment.lateSubmitDate)
    );

    // Utilize for cases where we should not show a StudentAssignmentPrimaryActionButton due to certain conditions of the given assignment.
    const shouldHideButton = !isBoolean(assignment.studentReview);

    // Reviewable: student can open the review player.
    if (assignment.studentReview && assignment.submitted && allowReview) {
      return { actionKey: 'launch', className: '', disabled: false, labelKey: 'review', lockedMessageKey: null };
    }

    // Late-submit still allowed on an otherwise-closed assignment.
    if (canTurnInLate) {
      return { actionKey: 'launch', className: 'turn-in-late', disabled: false, labelKey: 'turnInLate', lockedMessageKey: null };
    }

    // Not submitted (and no late-submit path) — matches old 'Locked' behavior.
    if (!assignment.submitted) {
      return { actionKey: null, className: 'pseudo-disabled', disabled: true, labelKey: 'locked', lockedMessageKey: 'closedLockedMessage' };
    }

    // Submitted PDF/DocReader with manual scoring awaiting grade — surface a
    // labeled disabled Review button with a message explaining the wait.
    // (Old implementation showed 'Locked' + closedLockedMessage, which was misleading.)
    if (showLockedUntilGraded && !allowReview) {
      return {
        actionKey: null,
        className: 'pseudo-disabled',
        disabled: true,
        labelKey: 'review',
        lockedMessageKey: 'lockedUntilGradedMessage'
      };
    }

    // If shouldHideButton is true, return a config that lets StudentAssignmentPrimaryActionButton know not to render it.
    if (shouldHideButton) {
      return { hidden: true };
    }
    // Submitted but the teacher has not enabled student review — surface a
    // labeled disabled Review button rather than hiding it entirely without context.
    // (Old implementation dropped the button, which could potentially be confusing.)
    return {
      actionKey: null,
      className: 'pseudo-disabled',
      disabled: true,
      labelKey: 'review',
      lockedMessageKey: 'completedLockedMessage'
    };
  }

  /**
   * Return the ASSIGNMENT_STATUS filter array for a given menu status. TODAY
   * uses a date filter (e.g. `dueToday: true`) rather than a status filter,
   * so it returns an empty array — callers handle the date filter separately.
   */
  static getStatusFilterForMenuStatus(menuStatus) {
    if (menuStatus === ASSIGNMENT_MENU_STATUS.CURRENT) {
      return [ASSIGNMENT_STATUS.LOCKED, ASSIGNMENT_STATUS.READY, ASSIGNMENT_STATUS.STARTED];
    }
    if (menuStatus === ASSIGNMENT_MENU_STATUS.CLOSED) {
      return [ASSIGNMENT_STATUS.CLOSED];
    }
    if (menuStatus === ASSIGNMENT_MENU_STATUS.GRADED) {
      return [ASSIGNMENT_STATUS.COMPLETED];
    }
    if (menuStatus === ASSIGNMENT_MENU_STATUS.TODAY) {
      // TODAY uses the dueToday flag + a dedicated endpoint, not a status filter.
      return [];
    }
    console.warn('[StudentAssignmentService] getStatusFilterForMenuStatus called with unknown menuStatus:', menuStatus);
    return [];
  }

  /**
   * Returns all fetch params (status, dueToday, filterDateStr) for a given student assignment menu status.
   * TODAY routes to `/api/viewDueTodayActivitiesByStudent` via `dueToday=true` + today filter;
   * all other tabs identify fully via status alone.
   * Defaults to CURRENT when menuStatus is falsy so callers do not need to guard.
   */
  static getFetchParamsForStudentAssignmentMenuStatus(menuStatus) {
    const effectiveMenuStatus = menuStatus || ASSIGNMENT_MENU_STATUS.CURRENT;
    const isDueTodayStatusTab = effectiveMenuStatus === ASSIGNMENT_MENU_STATUS.TODAY;
    return {
      status: this.getStatusFilterForMenuStatus(effectiveMenuStatus),
      dueToday: isDueTodayStatusTab,
      filterDateStr: isDueTodayStatusTab ? Auth.getToday() : null
    };
  }

  /**
   * Determine if the student is eligible for a Try Again attempt for a given assignment.
   */
  static isStudentAssignmentTryAgainEligible(assignment) {
    if (!assignment) {
      return false;
    }
    const numberOfActivities = 1;
    const hideTryAgain = false;
    return studentContentCardManager.getAllowStudentPacedAttempt(
      assignment,
      assignment.pacingModeOverride,
      assignment.attemptsOverride,
      numberOfActivities,
      hideTryAgain
    );
  }

  /**
   * Helper for determining and returning the grade info (grade text / key and className) for the student assignment card.
   */
  static getStudentAssignmentGradeInfo(assignment) {
    if (!assignment || !assignment.submitted || !assignment.scoresReleased) {
      return {};
    }
    const hasNumericGrade = typeof assignment.grade === 'number';
    if (hasNumericGrade) {
      return {
        assignmentGrade: `${assignment.grade}%`,
        assignmentGradeClassName: gradebookManager.getGradeClassName(assignment.grade)
      };
    }
    if (assignment.hasManualScoredItems) {
      return { assignmentGradeClassName: 'grade-low', assignmentGradeKey: 'waitingOnGrade' };
    }
    return { assignmentGradeClassName: 'grade-not-available', assignmentGradeKey: 'notAvailable' };
  }

  // ==== internal helpers ====

  static isLaunchableAssignment(assignment) {
    return ASSIGNMENT_STATUS.getCheckableStatuses().includes(assignment.status);
  }

  static getLaunchableAssignmentLabelKey(assignment, isFileResource) {
    if (isFileResource) {
      return 'download';
    }
    if (assignment.status === ASSIGNMENT_STATUS.STARTED) {
      return 'continue';
    }
    return 'start';
  }

  static getLockedMessageKey(assignment) {
    if (!assignment) {
      return null;
    }
    const startDate = assignment.timezoneStartDate ? new Date(assignment.timezoneStartDate).valueOf() : 0;
    if (startDate > Date.now()) {
      return 'lockedByDate';
    }
    return 'lockedByTeacher';
  }
}

registerClass('StudentAssignmentService', StudentAssignmentService);
